import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { createSelector } from '@ngrx/store';
import { IReceipt, ISelectedClientAccountsState } from '@shared/models';
import { ConfirmContent } from '@shared/services/content/content-jsons/confirm-content';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { getOpenAccountState } from '@store/reducers';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectDecedentFullName } from '@store/selectors/decedent-info/decedent-info.selectors';
import { selectEstateState } from '@store/selectors/estate/estate.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectSettlementFundChoice } from '@store/selectors/open-account/open-account.selectors';
import {
  selectAcceptedDAOffer,
  selectPortfolioMatchResult,
} from '@store/selectors/open-choice/open-choice.selectors';
import {
  selectedAccountsFrom,
  selectedClientAccountsState,
} from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { selectAccountType, selectTriageState } from '@store/selectors/triage/triage.selectors';
import {
  selectInvestmentStrategy,
  selectRetirementAge,
  selectTaxableIncomeString,
  selectTaxStatus,
} from 'vg-interaction-model';

import {
  buildAccountFromNonretirement,
  buildAccountToNonretirement,
} from '../cashier-nonretirement';
import {
  selectEmploymentReceipt,
  selectFinancialProfileReceipt,
  selectProfileReceipt,
} from '../new-account/new-account.selectors';

export const selectAccountFromMinorReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
) =>
  createSelector(
    selectedAccountsFrom,
    selectAccountType,
    selectTriageState,
    (state, accountType, triageState) =>
      buildAccountFromNonretirement(state, accountType, editable, triageState, content),
  );

export const selectAccountToMinorReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
) =>
  createSelector(
    selectMfePersonalInfoClientPersonalInfo,
    selectedClientAccountsState,
    selectTriageState,
    selectEstateState,
    selectDecedentFullName,
    getOpenAccountState,
    selectSettlementFundChoice,
    (
      personalInfo: MfePersonalInfoClientState['personalInfo'],
      selectedAccountState: ISelectedClientAccountsState,
      guideState: TriageState,
      estateState: EstateState,
      decedentName: string,
      openAccountState: IOpenAccountState,
      settlementFundChoice: SettlementFundAoAEnum | null,
    ) => {
      return buildAccountToNonretirement(
        personalInfo,
        selectedAccountState,
        guideState,
        estateState,
        decedentName,
        openAccountState,
        editable,
        settlementFundChoice,
        content,
      );
    },
  );

export const selectDigitalAdvisorReceipt = createSelector(
  selectTaxStatus,
  selectTaxableIncomeString,
  selectRetirementAge,
  selectPortfolioMatchResult,
  selectInvestmentStrategy,
  (taxStatus, taxableIncome, retirementAge, riskAttitude, investmentStrategy) => {
    const digitalAdvice: IReceipt = {
      header: 'Digital Advisor profile',
      editable: true,
      editRoute: `secure/open-choice/offer`,
      sectionEvidenceID: 'digitalAdvisorProfile',
      rows: [
        { label: 'Tax filing status', values: [taxStatus], evidenceID: 'taxFilingStatus' },
        {
          label: 'Estimated taxable income',
          values: [taxableIncome],
          evidenceID: 'estimatedTaxableIncome',
        },
        {
          label: 'Retirement age',
          values: [retirementAge?.toString()],
          evidenceID: 'retirementAge',
        },
        { label: 'Risk attitude', values: [riskAttitude], evidenceID: 'riskAttitude' },
        {
          label: 'Investment strategy',
          values: [investmentStrategy],
          evidenceID: 'investmentStrategy',
        },
      ],
    };

    return digitalAdvice;
  },
);

export const selectMinorReceiptData = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent | typeof ConfirmContent.receiptContent,
) =>
  createSelector(
    selectAccountFromMinorReceipt(editable, content?.transferFromAndTo),
    selectAccountToMinorReceipt(editable, content?.transferFromAndTo),
    selectProfileReceipt(editable, content?.profile),
    selectEmploymentReceipt(editable, content?.employment),
    selectFinancialProfileReceipt(editable, content?.financialProfile),
    selectDigitalAdvisorReceipt,
    selectAcceptedDAOffer,
    (from, to, profile, employment, financial, digitalAdvisor, acceptedDAOffer) => {
      if (acceptedDAOffer) {
        return {
          from,
          to,
          profile,
          employment,
          financial,
          digitalAdvisor,
        };
      }
      return {
        from,
        to,
        profile,
        employment,
        financial,
      };
    },
  );
